import React, { useState, useEffect } from 'react';
import { Search, UserPlus, LogOut as VacateIcon, ChevronDown, User } from 'lucide-react';
import { getHostels, getBlocks, getFloors, getRooms, getVacantBeds, allocateStudent, getAllocations, vacateStudent } from '../../../services/hostelService';
import { db } from '../../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import toast from 'react-hot-toast';

const BedAllocation = ({ coachingId }) => {
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [hostels, setHostels] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState('name');
  const [searching, setSearching] = useState(false);
  const [showDrop, setShowDrop] = useState(false);

  const [form, setForm] = useState({ studentId: '', studentName: '', hostelId: '', blockId: '', floorId: '', roomId: '', bedId: '' });

  useEffect(() => { loadAllocations(); loadHostels(); }, [coachingId]);

  const loadAllocations = async () => {
    setLoading(true);
    const data = await getAllocations(coachingId);
    setAllocations(data);
    setLoading(false);
  };

  const loadHostels = async () => {
    const data = await getHostels(coachingId);
    setHostels(data);
  };

  const handleHostelChange = async (hostelId) => {
    setForm({ ...form, hostelId, blockId: '', floorId: '', roomId: '', bedId: '' });
    const data = await getBlocks(hostelId);
    setBlocks(data); setFloors([]); setRooms([]); setBeds([]);
  };

  const handleBlockChange = async (blockId) => {
    setForm({ ...form, blockId, floorId: '', roomId: '', bedId: '' });
    const data = await getFloors(blockId);
    setFloors(data); setRooms([]); setBeds([]);
  };

  const handleFloorChange = async (floorId) => {
    setForm({ ...form, floorId, roomId: '', bedId: '' });
    const data = await getRooms(floorId);
    setRooms(data); setBeds([]);
  };

  const handleRoomChange = async (roomId) => {
    setForm({ ...form, roomId, bedId: '' });
    const data = await getVacantBeds(roomId);
    setBeds(data);
  };

  const searchStudents = async () => {
    if (!searchQuery.trim()) return toast.error('Enter a search value');
    setSearching(true);
    setShowDrop(false);
    try {
      let data = [];
      if (searchBy === 'regNumber') {
        // Search by registration number (exact or partial match)
        const snap = await getDocs(query(
          collection(db, 'students'),
          where('coachingId', '==', coachingId)
        ));
        const term = searchQuery.trim().toLowerCase();
        data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => (s.registrationNumber || '').toLowerCase().includes(term));
      } else {
        // Search by name
        const snap = await getDocs(query(
          collection(db, 'students'),
          where('coachingId', '==', coachingId)
        ));
        const term = searchQuery.trim().toLowerCase();
        data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => (s.name || s.fullName || '').toLowerCase().includes(term));
      }
      setStudents(data);
      setShowDrop(true);
      if (!data.length) toast('No students found', { icon: 'ℹ️' });
    } catch (e) {
      toast.error('Search failed: ' + e.message);
    } finally {
      setSearching(false);
    }
  };

  const handleAllocate = async (e) => {
    e.preventDefault();
    if (!form.studentId || !form.bedId) return toast.error('Select student and bed');
    await allocateStudent(coachingId, form);
    toast.success('Student allocated successfully');
    setShowForm(false);
    setForm({ studentId: '', studentName: '', hostelId: '', blockId: '', floorId: '', roomId: '', bedId: '' });
    loadAllocations();
  };

  const handleVacate = async (alloc) => {
    if (!window.confirm(`Vacate ${alloc.studentName}?`)) return;
    await vacateStudent(alloc.id, alloc.bedId, alloc.roomId, 'Vacated by admin');
    toast.success('Student vacated');
    loadAllocations();
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Active Allocations ({allocations.length})</h2>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          <UserPlus size={16} /> Allocate Student
        </button>
      </div>

      {/* Allocation Form */}
      {showForm && (
        <form onSubmit={handleAllocate} className="bg-white dark:bg-gray-800 border rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-gray-800 dark:text-white">New Allocation</h3>

          {/* Student Search */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Search Student</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <select value={searchBy} onChange={e => setSearchBy(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm cursor-pointer">
                  <option value="name">By Name</option>
                  <option value="regNumber">By Reg No</option>
                </select>
                <ChevronDown className="absolute right-2 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              <input type="text" value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchStudents())}
                placeholder={searchBy === 'name' ? 'Enter student name…' : 'Enter registration number…'}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              <button type="button" onClick={searchStudents} disabled={searching}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                {searching ? <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Search className="w-4 h-4" />}
                Search
              </button>
            </div>

            {showDrop && students.length > 0 && (
              <div className="mt-3 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-700 shadow-lg max-h-48 overflow-y-auto">
                {students.map(s => (
                  <button key={s.id} type="button" onClick={() => { setForm({...form, studentId: s.id, studentName: s.name || s.fullName || 'Student'}); setStudents([]); setShowDrop(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-left ${form.studentId === s.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                    {s.profilePicture
                      ? <img src={s.profilePicture} alt="" className="w-9 h-9 rounded-full object-cover border" />
                      : <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center"><User className="w-5 h-5 text-blue-600" /></div>
                    }
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">{s.name || s.fullName}</p>
                      <p className="text-xs text-gray-500">{s.email || '—'} · {s.registrationNumber || 'No Reg#'}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {form.studentId && <p className="text-sm text-green-600 mt-2 font-medium">Selected: {form.studentName}</p>}
          </div>

          {/* Hostel → Block → Floor → Room → Bed */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <select value={form.hostelId} onChange={e => handleHostelChange(e.target.value)} className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <option value="">Select Hostel</option>
              {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
            <select value={form.blockId} onChange={e => handleBlockChange(e.target.value)} className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={!form.hostelId}>
              <option value="">Select Block</option>
              {blocks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select value={form.floorId} onChange={e => handleFloorChange(e.target.value)} className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={!form.blockId}>
              <option value="">Select Floor</option>
              {floors.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <select value={form.roomId} onChange={e => handleRoomChange(e.target.value)} className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={!form.floorId}>
              <option value="">Select Room</option>
              {rooms.map(r => <option key={r.id} value={r.id}>Room {r.roomNumber} ({r.capacity - (r.occupiedBeds||0)} vacant)</option>)}
            </select>
            <select value={form.bedId} onChange={e => setForm({...form, bedId: e.target.value})} className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={!form.roomId}>
              <option value="">Select Bed</option>
              {beds.map(b => <option key={b.id} value={b.id}>Bed {b.bedNumber}</option>)}
            </select>
          </div>

          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">Allocate</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
          </div>
        </form>
      )}

      {/* Allocation List */}
      <div className="bg-white dark:bg-gray-800 border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Student</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Room</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Bed</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-gray-700">
              {allocations.map(a => (
                <tr key={a.id}>
                  <td className="px-4 py-3 text-gray-800 dark:text-white">{a.studentName || a.studentId}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{a.roomId?.substring(0,8)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{a.bedId?.substring(0,8)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleVacate(a)} className="text-red-500 hover:text-red-700 flex items-center gap-1 text-xs"><VacateIcon size={14} /> Vacate</button>
                  </td>
                </tr>
              ))}
              {allocations.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No active allocations</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BedAllocation;
